import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  Typography,
  Box,
  TextField,
  TablePagination,
  Alert,
  Snackbar,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTHAdminUser,
  saveTHAdminUsers,
} from "../../../../function-components/Redux/THUserMaintenance";
import { useMsal } from "@azure/msal-react";
import { updateLoginUserName } from "../../app/DeliveryLeadershipSlice";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";

const THUserMaintenance = ({ trackId, show }) => {
  const dispatch = useDispatch();
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const Users = useSelector((state) => state?.thAdminUser?.TrackAdminUsers);

  const [usersState, setUsersState] = useState([]);
  const [initialUsers, setInitialUsers] = useState([]);
  const [statusAlert, setStatusAlert] = useState({});
  const [statusOpen, setStatusOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (Array.isArray(Users)) {
      const deep = JSON.parse(JSON.stringify(Users));
      setUsersState(deep);
      setInitialUsers(JSON.parse(JSON.stringify(Users)));
    }
  }, [Users]);

  useEffect(() => {
    if (activeAccount) {
      dispatch(updateLoginUserName(activeAccount.idTokenClaims.enterpriseID));
    } else {
      dispatch(updateLoginUserName(null));
    }
  }, [activeAccount]);

  useEffect(() => {
    if (activeAccount && trackId) {
      dispatch(fetchTHAdminUser(trackId));
    }
  }, []);

  const getEditableUser = (enterpriseID, id) =>
    usersState.find(
      (u) => u.EnterpriseID === enterpriseID && String(u.id) === String(id)
    );

  const handleChange = (e, enterpriseId, id) => {
    const { name, checked } = e.target;

    setUsersState((prev) =>
      prev.map((u) => {
        if (u.EnterpriseID === enterpriseId && String(u.id) === String(id)) {
          let updatedRoles = u.roles?.map((r) => ({ ...r })) || [];

          if (name === "isActive") {
            updatedRoles = updatedRoles.map((role) =>
              role.role === "th" || role.role === "requestor"
                ? { ...role, status: checked }
                : role
            );
          } else {
            updatedRoles = updatedRoles.map((role) =>
              role.role === name ? { ...role, status: checked } : role
            );
          }

          return { ...u, roles: updatedRoles };
        }
        return u;
      })
    );
  };

  const handleReset = () => {
    setUsersState(JSON.parse(JSON.stringify(initialUsers)));
  };

  const handleSave = () => {
    try {
      const changedUsers = usersState.filter((user) => {
        const initial = initialUsers.find(
          (i) =>
            i.EnterpriseID === user.EnterpriseID &&
            String(i.id) === String(user.id)
        );
        if (!initial) return true;
        return JSON.stringify(user.roles) !== JSON.stringify(initial.roles);
      });

      if (changedUsers.length === 0) {
        setStatusAlert({ severity: "warn", message: "No changes to save!" });
        setStatusOpen(true);
        return;
      }

      dispatch(saveTHAdminUsers(changedUsers));

      setInitialUsers(JSON.parse(JSON.stringify(usersState)));
      setStatusOpen(true);
      setStatusAlert({
        severity: "success",
        message: "User privillages saved!",
      });
    } catch (err) {
      setStatusOpen(true);
      setStatusAlert({ severity: "error", message: "Save failed!" });
    }
  };
  const isEmptyAlertObject = (obj) => Object.keys(obj).length === 0;

  const handleAlertClose = () => {
    setStatusOpen(false);
  };

  // NOTE: tabs are filtered from initialUsers (saved state) — important!
  const activeUsers = (initialUsers || []).filter((user) =>
    user.roles?.some((r) => r.status === true)
  );
  const inactiveUsers = (initialUsers || []).filter((user) =>
    user.roles?.every((r) => r.status === false)
  );
  let usersToRender = show === "active" ? activeUsers : inactiveUsers;

  if (searchText.trim()) {
    usersToRender = usersToRender.filter((u) =>
      u.EnterpriseID.toLowerCase().includes(searchText.toLowerCase())
    );
  }
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  return (
    <Box sx={{ maxWidth: 900, margin: "20px auto" }}>
      {!isEmptyAlertObject(statusAlert) && statusAlert.severity && (
        <Snackbar
          open={statusOpen}
          autoHideDuration={5000}
          onClose={handleAlertClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert severity={statusAlert.severity}>{statusAlert.message}</Alert>
        </Snackbar>
      )}
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Search by Enterprise ID"
          variant="outlined"
          fullWidth
          size="small"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(0);
          }}
        />
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        <Table stickyHeader>
          <TableHead
            sx={{
              "& .MuiTableCell-head": {
                background: "#7d0b9cff",
                color: "white",
                fontWeight: "bold",
                fontSize: "0.9rem",
              },
            }}
          >
            <TableRow>
              <TableCell>Enterprise ID</TableCell>
              <TableCell
                align="center"
                sx={{ color: "white", fontWeight: "bold", fontSize: "1rem" }}
              >
                TH
              </TableCell>
              <TableCell
                align="center"
                sx={{ color: "white", fontWeight: "bold", fontSize: "1rem" }}
              >
                Requestor
              </TableCell>
              <TableCell
                align="center"
                sx={{ color: "white", fontWeight: "bold", fontSize: "1rem" }}
              >
                Active
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {usersToRender.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <DoNotDisturbIcon color="error" fontSize="small" />
                    {show === "active" ? (
                      <span>No active users.</span>
                    ) : (
                      <span>No inactive users.</span>
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {usersToRender
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((savedUser) => {
                const user =
                  getEditableUser(savedUser.EnterpriseID, savedUser.id) ||
                  savedUser;
                const thRole = user.roles?.find((r) => r.role === "th");
                const reqRole = user.roles?.find((r) => r.role === "requestor");
                const isActive =
                  thRole?.status === true && reqRole?.status === true;

                return (
                  <TableRow
                    key={`${savedUser.EnterpriseID}-${savedUser.id}`}
                    hover
                    sx={{
                      "& td, & th": { padding: "6px 12px" }, // compact spacing
                      "&:nth-of-type(even)": { backgroundColor: "#fafafa" },
                      "&:hover": {
                        backgroundColor: "#f3e5f5",
                        transition: "0.3s",
                        paddingLeft: "10px",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 500, color: "#333" }}
                      >
                        {savedUser.EnterpriseID}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Checkbox
                        name="th"
                        checked={!!thRole?.status}
                        onChange={(e) =>
                          handleChange(e, savedUser.EnterpriseID, savedUser.id)
                        }
                        sx={{
                          color: "#87069bff",
                          "&.Mui-checked": { color: "#87069bff" },
                        }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Checkbox
                        name="requestor"
                        checked={!!reqRole?.status}
                        onChange={(e) =>
                          handleChange(e, savedUser.EnterpriseID, savedUser.id)
                        }
                        sx={{
                          color: "#0288d1",
                          "&.Mui-checked": { color: "#0288d1" },
                        }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Checkbox
                        name="isActive"
                        checked={isActive}
                        onChange={(e) =>
                          handleChange(e, savedUser.EnterpriseID, savedUser.id)
                        }
                        sx={{
                          color: "#2e7d32",
                          "&.Mui-checked": { color: "#2e7d32" },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>

        {/* Pagination */}
        {usersToRender.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={usersToRender.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiTablePagination-toolbar": {
                backgroundColor: "#f5f5f5",
                borderTop: "1px solid #e0e0e0",
              },
            }}
          />
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 2,
            gap: 2,
            borderTop: "1px solid #eee",
            backgroundColor: "#fafafa",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleReset}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: "bold",
              background: "linear-gradient(90deg, #6a1b9a 0%, #9c27b0 100%)",
            }}
          >
            Save
          </Button>
        </Box>
      </TableContainer>
    </Box>
  );
};

export default THUserMaintenance;
